// File: src/components/payments/PaymentStatusDialog.jsx
// ---------------------------------------------------------------------------
// Shows current payment / trial state + arrangement billing tabs + "add / update card" + cancel
// Tabs: Status | [Clients] | Payments | Refunds | Discounts | OTC | Saved cycles
// Clients tab (accountant only): self-paid vs accountant-paid with credit-card / unpaid status
// ---------------------------------------------------------------------------

import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, Box, Typography, DialogContent,
  DialogActions, Button, Chip, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Tabs, Tab,
  TablePagination, TextField, InputAdornment, Link, Tooltip
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import PaymentIcon from '@mui/icons-material/Payment';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import SettingsIcon from '@mui/icons-material/Settings';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PeopleIcon from '@mui/icons-material/People';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

import UpdateOrAddPaymentMethod from './UpdateOrAddPaymentMethod.jsx';
import MasavMethodEditor from './MasavMethodEditor.jsx';
import ChargesDetailsPanel from './ChargesDetailsPanel.jsx';
import PaymentService from '../../Services/payment.service.js';
import ClientService from '../../Services/client.service.js';
import Constants from '../../assests/Constants/constants.js';

/* ───────────────────────── helpers ───────────────────────── */
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const fmtCurrency = (amount) =>
  (amount != null && amount !== '')
    ? new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(Number(amount))
    : '—';

const getPaymentMethodText = (paymentMethodEnum) => {
  const paymentMethods = {
    0: 'BankTransfer',
    1: 'CreditCard',
    2: 'Cash',
    3: 'PayPal',
    4: 'Check',
    5: 'Other'
  };
  return paymentMethods[paymentMethodEnum] ?? paymentMethods[String(paymentMethodEnum)] ?? 'Other';
};

const getBillingPlanText = (billingPlanEnum) => {
  const billingPlans = { '-1': 'NotSet', 0: 'Trial', 1: 'Monthly', 2: 'Yearly' };
  return billingPlans[billingPlanEnum] ?? billingPlans[String(billingPlanEnum)] ?? 'NotSet';
};

const getAccountantPaymentTypeText = (paymentTypeEnum) => {
  const paymentTypes = { 0: 'Package', 1: 'Monthly' };
  return paymentTypes[paymentTypeEnum] ?? paymentTypes[String(paymentTypeEnum)] ?? 'Package';
};

/** Normalize DTO: API may return PascalCase or camelCase */
const arr = (obj, key) => {
  const k = key.charAt(0).toLowerCase() + key.slice(1);
  const p = key.charAt(0).toUpperCase() + key.slice(1);
  return obj?.[k] ?? obj?.[p] ?? [];
};

const val = (obj, key) => {
  if (obj == null) return undefined;
  const k = key.charAt(0).toLowerCase() + key.slice(1);
  const p = key.charAt(0).toUpperCase() + key.slice(1);
  return obj[k] ?? obj[p];
};

const STYLE = {
  position: 'absolute',
  top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '96%', maxWidth: 1100,
  bgcolor: 'background.paper',
  p: 4, borderRadius: 3, boxShadow: 24
};

const cellSx = { py: 0.5, px: 1, fontSize: 12, whiteSpace: 'nowrap' };
const headerSx = { ...cellSx, fontWeight: 700 };

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const getNetBalanceColor = (balance) => {
  if (balance == null || balance === '') return 'inherit';
  const n = Number(balance);
  if (n > 0) return '#c62828';
  if (n < 0) return '#2e7d32';
  return 'inherit';
};

const currentToMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const SummaryTile = ({ label, value, color }) => (
  <Paper elevation={0} sx={{ p: 1.5, textAlign: 'center', flex: 1, minWidth: 90, border: '1px solid #e0e0e0', borderRadius: 1 }}>
    <Typography variant="caption" sx={{ fontSize: 10, color: '#666' }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13, color: color || 'inherit', direction: 'ltr' }}>
      {typeof value === 'number' ? fmtCurrency(value) : value ?? '—'}
    </Typography>
  </Paper>
);

/** ClientPaymentMethod: 0 / '0' = accountant pays, 1 / '1' = client pays */
const isClientPayer = (clientPaymentMethod) => {
  const v = clientPaymentMethod;
  return v === 1 || v === '1' || String(v) === Constants.ClientPaymentMethod?.Client;
};

const kindOf = (doc) => val(doc, 'kindString');

/** Invoice / credit-note reference that opens the document in a new tab. */
const DocumentLink = ({ doc, color, label, onOpen }) => (
  <Link
    component="button"
    type="button"
    underline="hover"
    onClick={(e) => { e.stopPropagation(); onOpen(doc); }}
    sx={{ fontSize: 12, fontWeight: 600, color }}
  >
    {val(doc, 'reference') || label}
  </Link>
);

/* ───────────────────────── component ──────────────────────── */
const PaymentStatusDialog = ({
  open, onClose,
  status, loading, error,
  onCancel, cancelling,
  userId, userType,
  refreshStatus
}) => {
  const { t, i18n } = useTranslation();
  const dir = i18n.dir();
  const isAccountant = userType === 2;
  const [tabKey, setTabKey] = useState('status');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [excelDownloading, setExcelDownloading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [arrangement, setArrangement] = useState(null);
  const [arrangementLoading, setArrangementLoading] = useState(false);
  const [calcData, setCalcData] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [officeClients, setOfficeClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState(null);
  const [clientsSearch, setClientsSearch] = useState('');
  const [clientsSubTab, setClientsSubTab] = useState(0); // 0 = self-paid, 1 = accountant-paid
  const [clientStatusByCaseId, setClientStatusByCaseId] = useState({});
  const [clientsExportingPdf, setClientsExportingPdf] = useState(false);
  const [clientsExportingExcel, setClientsExportingExcel] = useState(false);
  const [documents, setDocuments] = useState(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  const planTypeLabel = (planType) => {
    if (!planType) return '—';
    return planType === 'Monthly' ? t('MonthlyPlan') : planType === 'Package' ? t('PackagePlan') : planType;
  };

  useEffect(() => {
    if (open && userId) loadArrangement();
  }, [open, userId]);

  useEffect(() => {
    if (!open) {
      setTabKey('status');
      setClientsSearch('');
      setClientsSubTab(0);
      setPage(0);
      setDocuments(null);
    }
  }, [open]);

  const needsDocuments = open && !!userId && (tabKey === 'payments' || tabKey === 'refunds');
  useEffect(() => {
    if (!needsDocuments || documents !== null) return undefined;
    let cancelled = false;
    setDocumentsLoading(true);
    (isAccountant
      ? PaymentService.accountant.getPaymentDocuments(userId)
      : PaymentService.client.getPaymentDocuments(userId))
      .then((rows) => { if (!cancelled) setDocuments(rows); })
      .catch((err) => {
        if (!cancelled) setDocuments([]);
        console.error('Error loading payment documents:', err);
      })
      .finally(() => { if (!cancelled) setDocumentsLoading(false); });
    return () => { cancelled = true; };
  }, [needsDocuments, documents, isAccountant, userId]);

  const { documentsByPayment, documentsByRefund } = useMemo(() => {
    const byPayment = {};
    const byRefund = {};
    (documents || []).forEach((doc) => {
      const paymentId = val(doc, 'billingPaymentId');
      byPayment[paymentId] = [...(byPayment[paymentId] || []), doc];
      const refundId = val(doc, 'billingRefundId');
      if (refundId) byRefund[refundId] = [...(byRefund[refundId] || []), doc];
    });
    return { documentsByPayment: byPayment, documentsByRefund: byRefund };
  }, [documents]);

  /** Opens the tab synchronously (so popup blockers allow it), then points it at the presigned URL. */
  const openDocument = async (doc) => {
    const win = window.open('', '_blank');
    try {
      const url = isAccountant
        ? await PaymentService.accountant.getPaymentDocumentUrl(userId, val(doc, 'id'))
        : await PaymentService.client.getPaymentDocumentUrl(userId, val(doc, 'id'));
      if (!url) throw new Error('No URL');
      if (win) win.location.href = url;
      else window.open(url, '_blank', 'noopener');
    } catch (err) {
      win?.close();
      setPdfError(t('DownloadFailed') || 'Download failed');
    }
  };

  const renderDocumentsPlaceholder = () => (documentsLoading
    ? <CircularProgress size={12} />
    : <Typography component="span" sx={{ fontSize: 12, color: '#999' }}>—</Typography>);

  const renderPaymentDocuments = (paymentId) => {
    const docs = documentsByPayment[paymentId] || [];
    const invoice = docs.find((d) => kindOf(d) === 'InvoiceReceipt');
    const creditNotes = docs.filter((d) => kindOf(d) === 'CreditNote');
    if (!invoice && creditNotes.length === 0) return renderDocumentsPlaceholder();
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        {invoice && <DocumentLink doc={invoice} color="#304FFF" label={t('Invoice')} onOpen={openDocument} />}
        {creditNotes.map((cn) => (
          <Tooltip key={val(cn, 'id')} title={`${t('CreditNote')}${val(cn, 'reference') ? ` ${val(cn, 'reference')}` : ''}`}>
            <Chip
              size="small"
              clickable
              label={t('CreditNote')}
              onClick={(e) => { e.stopPropagation(); openDocument(cn); }}
              sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: 'rgba(229,62,62,0.12)', color: '#E53E3E' }}
            />
          </Tooltip>
        ))}
      </Box>
    );
  };

  const renderRefundDocuments = (refundId) => {
    const creditNotes = (documentsByRefund[refundId] || []).filter((d) => kindOf(d) === 'CreditNote');
    if (creditNotes.length === 0) return renderDocumentsPlaceholder();
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        {creditNotes.map((cn) => (
          <DocumentLink key={val(cn, 'id')} doc={cn} color="#E53E3E" label={t('CreditNote')} onOpen={openDocument} />
        ))}
      </Box>
    );
  };

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    (async () => {
      setCalcLoading(true);
      setCalcData(null);
      const to = currentToMonthKey();
      try {
        const data = isAccountant
          ? await PaymentService.accountant.getCalculation(userId, null, to)
          : await PaymentService.client.getCalculation(userId, null, to);
        if (!cancelled) setCalcData(data);
      } catch (err) {
        if (!cancelled) setCalcData(null);
        if (err?.message && !String(err.message).includes('404')) console.error('Error loading billing calculation:', err);
      } finally {
        if (!cancelled) setCalcLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, userId, userType]);

  useEffect(() => {
    if (!open || !userId || !isAccountant) {
      setOfficeClients([]);
      setClientStatusByCaseId({});
      setClientsError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setClientsLoading(true);
      setClientsError(null);
      try {
        const raw = localStorage.getItem('token');
        const token = raw ? JSON.parse(raw) : null;
        const data = await ClientService.GetMinimalClients(userId, token, 1, 2000, true);
        const list = data?.clients ?? data?.Clients ?? [];
        if (cancelled) return;
        setOfficeClients(list);

        const selfPaidCaseIds = list
          .filter((c) => isClientPayer(c.clientPaymentMethod ?? c.ClientPaymentMethod))
          .map((c) => c.caseId ?? c.CaseId)
          .filter(Boolean);
        if (selfPaidCaseIds.length > 0) {
          try {
            const statuses = await PaymentService.client.statusBatch(selfPaidCaseIds);
            if (!cancelled) setClientStatusByCaseId(statuses || {});
          } catch (statusErr) {
            if (!cancelled) setClientStatusByCaseId({});
            console.error('Error loading client payment statuses:', statusErr);
          }
        } else {
          setClientStatusByCaseId({});
        }
      } catch (err) {
        if (!cancelled) {
          setOfficeClients([]);
          setClientsError(err?.message || t('fetchClientsDataError') || 'Failed to load clients');
        }
        console.error('Error loading office clients:', err);
      } finally {
        if (!cancelled) setClientsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, userId, isAccountant]);

  const loadArrangement = async () => {
    setArrangementLoading(true);
    setArrangement(null);
    try {
      const data = isAccountant
        ? await PaymentService.accountant.getArrangement(userId)
        : await PaymentService.client.getArrangement(userId);
      setArrangement(data);
    } catch (err) {
      if (err?.message && !err.message.includes('404')) console.error('Error loading arrangement:', err);
    } finally {
      setArrangementLoading(false);
    }
  };

  const payments = useMemo(() => arr(arrangement || {}, 'payments'), [arrangement]);
  const refunds = useMemo(() => arr(arrangement || {}, 'refunds'), [arrangement]);
  const discounts = useMemo(() => arr(arrangement || {}, 'discounts'), [arrangement]);
  const onTimeCharges = useMemo(() => arr(arrangement || {}, 'onTimeCharges'), [arrangement]);
  const planPeriods = useMemo(() => arr(arrangement || {}, 'planPeriods'), [arrangement]);

  const sortedPlanPeriods = useMemo(() => [...planPeriods].sort((a, b) => {
    const aStart = val(a, 'startMonthKey') || val(a, 'startDateFormatted') || '';
    const bStart = val(b, 'startMonthKey') || val(b, 'startDateFormatted') || '';
    return String(bStart).localeCompare(String(aStart));
  }), [planPeriods]);

  const calcMonthsCount = calcData?.months?.length ?? 0;

  const paymentsTotal = useMemo(() => payments.reduce((sum, p) => sum + (Number(val(p, 'amount')) || 0), 0), [payments]);
  const refundsTotal = useMemo(() => refunds.reduce((sum, r) => sum + (Number(val(r, 'amount')) || 0), 0), [refunds]);
  const otcTotal = useMemo(() => onTimeCharges.reduce((sum, o) => sum + (Number(val(o, 'amount')) || 0), 0), [onTimeCharges]);

  const calcTotals = calcData?.totals ?? calcData?.Totals;
  const clientPayersTotals = calcData?.clientPayersTotals ?? calcData?.ClientPayersTotals;

  // Arrangement-derived fields for Status tab (same source as ViewEnhanced: getArrangement = by-accountant / by-client)
  const activePlanPeriod = useMemo(() => {
    const periods = planPeriods.filter((p) => val(p, 'isActive'));
    return periods.length ? periods[periods.length - 1] : null;
  }, [planPeriods]);
  const activePlanName = useMemo(
    () => (activePlanPeriod ? (val(activePlanPeriod, 'monthlyPlanName') || val(activePlanPeriod, 'packagePlanName') || '') : ''),
    [activePlanPeriod]
  );
  const uploadRule = useMemo(() => {
    if (!isAccountant || !arrangement || !activePlanPeriod) return null;
    const rawLimit = Number(val(arrangement, 'averageUploadsLimit'));
    const latest = [...(calcData?.months ?? [])]
      .filter((m) => val(val(m, 'aggregatedCycle'), 'uploadAverageLimit') != null)
      .sort((a, b) => String(val(b, 'monthKey')).localeCompare(String(val(a, 'monthKey'))))[0];
    return {
      enabled: !!val(activePlanPeriod, 'isChargableIfExceededMaxUploads'),
      limit: Number.isFinite(rawLimit) ? rawLimit : 100,
      maxUploads: val(activePlanPeriod, 'maxUploads') ?? status?.maxUploads ?? 150,
      unitPrice: val(activePlanPeriod, 'maxUploadsPrice') ?? 0.2,
      latestAgg: latest ? val(latest, 'aggregatedCycle') : null,
    };
  }, [isAccountant, arrangement, activePlanPeriod, calcData, status]);
  const hasCreditCard = useMemo(
    () => !!(val(arrangement, 'paymentMethodId') || val(arrangement, 'paymentMethodDetails')),
    [arrangement]
  );
  const arrangementNextBillingOn = arrangement ? val(arrangement, 'nextBillingOn') : null;
  const arrangementIsBlocked = arrangement ? val(arrangement, 'isBlocked') : null;
  const arrangementPlanString = arrangement ? val(arrangement, 'planString') : null;
  const arrangementLastPaymentDate = arrangement ? val(arrangement, 'lastPaymentDate') : null;
  const arrangementLastPaymentAmount = arrangement ? val(arrangement, 'lastPaymentAmount') : null;
  const displayName = isAccountant ? (val(arrangement, 'accountantName') || val(arrangement, 'name')) : (val(arrangement, 'clientName') || val(arrangement, 'name'));
  const displayId = isAccountant ? (val(arrangement, 'accountantId') || val(arrangement, 'payerAccountantId')) : (val(arrangement, 'clientCaseId') || val(arrangement, 'payerClientCaseId'));
  const displayPhone = isAccountant ? val(arrangement, 'phoneNumber') : val(arrangement, 'clientPhoneNumber');

  const tabs = useMemo(() => {
    const list = [{ key: 'status', label: t('Status') }];
    if (isAccountant) {
      list.push({ key: 'clients', label: `${t('Clients')} (${officeClients.length})` });
    }
    list.push(
      { key: 'payments', label: t('Payments') },
      { key: 'refunds', label: t('Refunds') },
      { key: 'discounts', label: t('Discounts') },
      { key: 'otc', label: t('OneTimeCharges') || 'חיובים חד פעמיים' },
      { key: 'cycles', label: t('SavedCycles') || 'מחזורים שמורים' }
    );
    return list;
  }, [isAccountant, officeClients.length, t]);

  const tabIndex = Math.max(0, tabs.findIndex((tab) => tab.key === tabKey));

  const selfPaidClients = useMemo(
    () => officeClients.filter((c) => isClientPayer(c.clientPaymentMethod ?? c.ClientPaymentMethod)),
    [officeClients]
  );
  const accountantPaidClients = useMemo(
    () => officeClients.filter((c) => !isClientPayer(c.clientPaymentMethod ?? c.ClientPaymentMethod)),
    [officeClients]
  );

  const filteredClientsForSubTab = useMemo(() => {
    const source = clientsSubTab === 0 ? selfPaidClients : accountantPaidClients;
    const q = clientsSearch.trim().toLowerCase();
    if (!q) return source;
    return source.filter((c) => {
      const name = (c.name ?? c.Name ?? '').toLowerCase();
      const caseId = String(c.caseId ?? c.CaseId ?? '');
      const caseType = (c.caseType ?? c.CaseType ?? '').toLowerCase();
      return name.includes(q) || caseId.includes(q) || caseType.includes(q);
    });
  }, [clientsSubTab, selfPaidClients, accountantPaidClients, clientsSearch]);

  const paginatedClients = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredClientsForSubTab.slice(start, start + rowsPerPage);
  }, [filteredClientsForSubTab, page, rowsPerPage]);

  const badge = (s) => {
    if (!s) return null;
    if (s.isBlocked) return <Chip color="error" icon={<CancelIcon />} label={t('Blocked')} />;
    if (s.isTrialActive) return <Chip color="warning" icon={<InfoOutlined />} label={t('Trial')} />;
    return <Chip color="success" icon={<CheckCircle />} label={t('Active')} />;
  };

  const handleTabChange = (event, newIndex) => {
    const next = tabs[newIndex]?.key || 'status';
    setTabKey(next);
    setPage(0);
  };

  const renderClientPaymentStatusChip = (client) => {
    const caseId = client.caseId ?? client.CaseId;
    const hasCard = !!(client.hasCreditCard ?? client.HasCreditCard);
    const st = clientStatusByCaseId?.[caseId] || clientStatusByCaseId?.[String(caseId)];
    const blocked = !!(st?.isBlocked ?? st?.IsBlocked);
    if (blocked) {
      return <Chip size="small" color="error" icon={<CancelIcon sx={{ fontSize: 14 }} />} label={t('Blocked')} sx={{ height: 22, fontSize: 11 }} />;
    }
    if (hasCard) {
      return <Chip size="small" color="success" icon={<CreditCardIcon sx={{ fontSize: 14 }} />} label={t('Yes')} sx={{ height: 22, fontSize: 11 }} />;
    }
    return <Chip size="small" color="warning" label={t('unpaid') || 'לא שולם'} sx={{ height: 22, fontSize: 11 }} />;
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleDownloadPdf = async () => {
    if (!userId) return;
    setPdfError(null);
    setPdfDownloading(true);
    try {
      const blob = isAccountant
        ? await PaymentService.accountant.exportPdf(userId)
        : await PaymentService.client.exportPdf(userId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Xtram_Statement_${userId}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setPdfError(err?.message || t('DownloadFailed') || 'Download failed');
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!userId) return;
    setPdfError(null);
    setExcelDownloading(true);
    try {
      const blob = isAccountant
        ? await PaymentService.accountant.exportExcel(userId)
        : await PaymentService.client.exportExcel(userId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Xtram_Statement_${userId}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setPdfError(err?.message || t('DownloadFailed') || 'Download failed');
    } finally {
      setExcelDownloading(false);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportClientsPdf = async () => {
    if (!userId || !isAccountant) return;
    setPdfError(null);
    setClientsExportingPdf(true);
    try {
      const blob = await PaymentService.accountant.exportClientsPdf(userId);
      downloadBlob(blob, `Xtram_Accountant_Clients_${userId}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      setPdfError(err?.message || t('DownloadFailed') || 'Download failed');
    } finally {
      setClientsExportingPdf(false);
    }
  };

  const handleExportClientsExcel = async () => {
    if (!userId || !isAccountant) return;
    setPdfError(null);
    setClientsExportingExcel(true);
    try {
      const blob = await PaymentService.accountant.exportClientsExcel(userId);
      downloadBlob(blob, `Xtram_Accountant_Clients_${userId}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      setPdfError(err?.message || t('DownloadFailed') || 'Download failed');
    } finally {
      setClientsExportingExcel(false);
    }
  };

  const paginatedPayments = useMemo(() => {
    const start = page * rowsPerPage;
    return payments.slice(start, start + rowsPerPage);
  }, [payments, page, rowsPerPage]);
  const paginatedRefunds = useMemo(() => {
    const start = page * rowsPerPage;
    return refunds.slice(start, start + rowsPerPage);
  }, [refunds, page, rowsPerPage]);
  const paginatedDiscounts = useMemo(() => {
    const start = page * rowsPerPage;
    return discounts.slice(start, start + rowsPerPage);
  }, [discounts, page, rowsPerPage]);
  const paginatedOtc = useMemo(() => {
    const start = page * rowsPerPage;
    return onTimeCharges.slice(start, start + rowsPerPage);
  }, [onTimeCharges, page, rowsPerPage]);

  return (
    <Modal open={open} onClose={onClose} disableEscapeKeyDown>
      <Box sx={STYLE} dir={dir}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <PaymentIcon /> {t('SubscriptionStatus')} {badge(status)}
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
          <Tabs value={tabIndex} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            {tabs.map((tab) => (
              <Tab
                key={tab.key}
                label={tab.key === 'clients'
                  ? (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <PeopleIcon sx={{ fontSize: 16 }} /> {tab.label}
                    </Box>
                  )
                  : tab.label}
              />
            ))}
          </Tabs>
        </Box>

        <DialogContent dividers sx={{ mt: 1, minHeight: 320, maxHeight: '70vh', overflow: 'auto' }}>
          {loading && (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {pdfError && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setPdfError(null)}>{pdfError}</Alert>}

          {isAccountant && status && status.consecutiveFailures > 0 && status.nextBillingOn && (() => {
            const nextBillingDate = new Date(status.nextBillingOn);
            const daysUntilBilling = Math.ceil((nextBillingDate - new Date()) / (1000 * 60 * 60 * 24));
            if (daysUntilBilling >= 7) return null;
            return (
              <Alert severity="error" sx={{ mb: 2 }} icon={<CancelIcon />}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>{t('LastTransactionDeclined')}</Typography>
                <Typography variant="body2">{t('CreditCardRejected')} • {t('UpdateCardDetailsToPreventBlocking')}</Typography>
              </Alert>
            );
          })()}

          {/* ───── Tab: Status ───── */}
          {tabKey === 'status' && status && !loading && (
            <Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <Paper elevation={1} sx={{ p: 2, borderTop: '3px solid #304FFF' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, fontSize: '1rem', color: '#304FFF', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SettingsIcon sx={{ fontSize: 18 }} /> {t('ArrangementDetails') || 'פרטי הסדר'}
                  </Typography>
                  {(displayId != null || displayName) && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                      {displayId != null && <Box><Typography variant="caption" color="text.secondary">{'מזהה' }</Typography><Typography variant="body2" fontWeight={600}>{displayId}</Typography></Box>}
                      {displayName != null && <Box><Typography variant="caption" color="text.secondary">{t('Name') || 'שם'}</Typography><Typography variant="body2" fontWeight={600}>{displayName || '—'}</Typography></Box>}
                    </Box>
                  )}
                  {displayPhone && (
                    <Box sx={{ mb: 1, pb: 1, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                      <Typography variant="caption" color="text.secondary">{t('Phone') || 'טלפון'}</Typography>
                      <Typography variant="body2">{displayPhone}</Typography>
                    </Box>
                  )}
                  <Box sx={{ mb: 1 }}><Typography variant="caption" color="text.secondary">{t('CreditCard') || 'כרטיס אשראי'}</Typography><Typography variant="body2"><Chip size="small" color={hasCreditCard ? 'success' : 'error'} label={hasCreditCard ? t('Yes') : t('No')} /></Typography></Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                    <Box><Typography variant="caption" color="text.secondary">{t('Plan')}</Typography><Typography variant="body2" fontWeight={600}>{status.isTrialActive ? t('Trial') : (arrangementPlanString ? t(arrangementPlanString) : (status.hasArrangementRow ? ((arrangementIsBlocked ?? status.isBlocked) ? t('Blocked') : t('Paid')) : t('NotSet')))}</Typography></Box>
                    <Box><Typography variant="caption" color="text.secondary">{t('NextBillingOn')}</Typography><Typography variant="body2">{fmtDate(arrangementNextBillingOn ?? status.nextBillingOn) || '—'}</Typography></Box>
                    <Box><Typography variant="caption" color="text.secondary">{t('GraceDaysLeft')}</Typography><Typography variant="body2">{status.graceDaysLeft ?? '—'}</Typography></Box>
                    <Box><Typography variant="caption" color="text.secondary">{t('PaymentMethodStored')}</Typography><Typography variant="body2">{(arrangement ? hasCreditCard : status.hasArrangementPaymentMethod) ? t('Yes') : t('No')}</Typography></Box>
                    <Box><Typography variant="caption" color="text.secondary">{t('CurrentPlan')}</Typography><Typography variant="body2">{arrangement ? (activePlanName || (val(arrangement, 'plan') != null ? t(getBillingPlanText(val(arrangement, 'plan'))) : '—')) : (status.currentPlan != null ? t(getBillingPlanText(status.currentPlan)) : '—')}</Typography></Box>
                    <Box><Typography variant="caption" color="text.secondary">{t('Status')}</Typography><Typography variant="body2"><Chip size="small" color={(arrangementIsBlocked ?? status.isBlocked) ? 'error' : 'success'} label={(arrangementIsBlocked ?? status.isBlocked) ? t('Blocked') : t('Active')} />{((val(arrangement, 'consecutiveFailures') ?? status?.consecutiveFailures) || 0) > 0 && <Chip size="small" color="error" sx={{ ml: 0.5 }} label={`${val(arrangement, 'consecutiveFailures') ?? status?.consecutiveFailures} ${t('Failures') || 'כשלונות'}`} />}</Typography></Box>
                  </Box>
                  {(arrangementLastPaymentDate || arrangementLastPaymentAmount != null) && (
                    <Box sx={{ mb: 0.5 }}><Typography variant="caption" color="text.secondary">{t('LastPayment') || 'תשלום אחרון'}</Typography><Typography variant="body2">{fmtDate(arrangementLastPaymentDate)} — {fmtCurrency(arrangementLastPaymentAmount)}</Typography></Box>
                  )}
                  {isAccountant && status.paymentType != null && <Box sx={{ mt: 0.5 }}><Typography variant="caption" color="text.secondary">{t('PaymentType')}</Typography><Typography variant="body2">{t(getAccountantPaymentTypeText(status.paymentType))}</Typography></Box>}
                  {status.packageCost != null && <Box sx={{ mt: 0.5 }}><Typography variant="caption" color="text.secondary">{t('PackageCost')}</Typography><Typography variant="body2">{fmtCurrency(status.packageCost)}</Typography></Box>}
                  {isAccountant && status.monthlyCost != null && <Box sx={{ mt: 0.5 }}><Typography variant="caption" color="text.secondary">{t('MonthlyCost')}</Typography><Typography variant="body2">{fmtCurrency(status.monthlyCost)}</Typography></Box>}
                  {isAccountant && status.maxClients != null && <Box sx={{ mt: 0.5 }}><Typography variant="caption" color="text.secondary">{t('MaxClients')}</Typography><Typography variant="body2">{status.maxClients}</Typography></Box>}
                  {status.maxUploads != null && <Box sx={{ mt: 0.5 }}><Typography variant="caption" color="text.secondary">{t('MaxUploads')}</Typography><Typography variant="body2">{status.maxUploads}</Typography></Box>}
                </Paper>
                <Paper elevation={1} sx={{ p: 2, borderTop: '3px solid #304FFF' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: '1rem', color: '#304FFF', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarTodayIcon sx={{ fontSize: 18 }} /> {t('CurrentPlan') || 'תוכנית נוכחית'}
                  </Typography>
                  {activePlanPeriod ? (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box><Typography variant="caption" color="text.secondary">{t('Type') || 'סוג'}</Typography><Typography variant="body2"><Chip size="small" color={val(activePlanPeriod, 'monthlyPlanId') ? 'info' : 'success'} label={val(activePlanPeriod, 'monthlyPlanId') ? (t('MonthlyPlan') || 'חודשי') : (t('PackagePlan') || 'חבילה')} /></Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">{t('PlanName') || 'שם תוכנית'}</Typography><Typography variant="body2" fontWeight={600}>{activePlanName || '—'}</Typography></Box>
                    </Box>
                  ) : (status.packagePlanName || status.monthlyPlanName) ? (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box><Typography variant="caption" color="text.secondary">{t('Type') || 'סוג'}</Typography><Typography variant="body2">{status.packagePlanName ? t('PackagePlan') : t('MonthlyPlan')}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">{t('PlanName') || 'שם תוכנית'}</Typography><Typography variant="body2">{status.packagePlanName || status.monthlyPlanName || '—'}</Typography></Box>
                    </Box>
                  ) : (
                    <Alert severity="info" sx={{ py: 0.5 }}><Typography variant="caption">{t('NoActivePlan') || 'אין תוכנית פעילה'}</Typography></Alert>
                  )}
                </Paper>
              </Box>
              {arrangement && (
                <>
                  {calcLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={28} /></Box>
                  )}
                  {!calcLoading && isAccountant && (calcTotals || clientPayersTotals) && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, fontSize: 14, color: '#1976d2' }}>
                        {t('arrangementView.chargesDetails.accountantPayerTab') || 'רו״ח משלם'}
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        <Paper elevation={1} sx={{ p: 2, borderTop: '3px solid #1976d2' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: 13 }}>סיכום רואה חשבון</Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <SummaryTile label={t('Charges') || 'חיובים'} value={calcTotals?.totalCharges ?? calcTotals?.TotalCharges} />
                            <SummaryTile label={t('OneTimeCharges') || 'חד פעמי'} value={calcTotals?.totalOneTimeCharges ?? calcTotals?.TotalOneTimeCharges} />
                            <SummaryTile label={t('Refunds') || 'החזרים'} value={calcTotals?.totalRefunds ?? calcTotals?.TotalRefunds} />
                            <SummaryTile label={t('Discounts') || 'הנחות'} value={calcTotals?.totalDiscounts ?? calcTotals?.TotalDiscounts} />
                            <SummaryTile label={t('Payments') || 'תשלומים'} value={calcTotals?.totalPayments ?? calcTotals?.TotalPayments} />
                            <SummaryTile label={t('NetBalance') || 'יתרה'} value={calcTotals?.netBalance ?? calcTotals?.NetBalance} color={getNetBalanceColor(calcTotals?.netBalance ?? calcTotals?.NetBalance)} />
                          </Box>
                        </Paper>
                        <Paper elevation={1} sx={{ p: 2, borderTop: '3px solid #ed6c02' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: 13 }}>{t('arrangementView.chargesDetails.clientPayerTab') || 'לקוח משלם'}</Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <SummaryTile label={t('Charges') || 'חיובים'} value={clientPayersTotals?.totalCharges ?? clientPayersTotals?.TotalCharges} />
                            <SummaryTile label={t('OneTimeCharges') || 'חד פעמי'} value={clientPayersTotals?.totalOneTimeCharges ?? clientPayersTotals?.TotalOneTimeCharges} />
                            <SummaryTile label={t('Refunds') || 'החזרים'} value={clientPayersTotals?.totalRefunds ?? clientPayersTotals?.TotalRefunds} />
                            <SummaryTile label={t('Discounts') || 'הנחות'} value={clientPayersTotals?.totalDiscounts ?? clientPayersTotals?.TotalDiscounts} />
                            <SummaryTile label={t('Payments') || 'תשלומים'} value={clientPayersTotals?.totalPayments ?? clientPayersTotals?.TotalPayments} />
                            <SummaryTile label={t('NetBalance') || 'יתרה'} value={clientPayersTotals?.netBalance ?? clientPayersTotals?.NetBalance} color={getNetBalanceColor(clientPayersTotals?.netBalance ?? clientPayersTotals?.NetBalance)} />
                          </Box>
                        </Paper>
                      </Box>
                    </Box>
                  )}
                  {uploadRule && (
                    <Paper elevation={1} sx={{ p: 2, mb: 2, borderTop: '3px solid #ed6c02' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: 13 }}>{t('UploadChargeRuleTitle')}</Typography>
                      {!uploadRule.enabled ? (
                        <Typography variant="body2" color="text.secondary">{t('UploadChargeRuleOff')}</Typography>
                      ) : (
                        <>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                            <SummaryTile label={t('MaxUploadsPerBusiness')} value={String(uploadRule.maxUploads)} />
                            <SummaryTile label={t('UploadUnitPriceBeforeVat')} value={Number(uploadRule.unitPrice)} />
                            <SummaryTile label={t('AverageUploadsLimit')} value={uploadRule.limit === 0 ? '—' : String(uploadRule.limit)} />
                            {uploadRule.latestAgg && (() => {
                              const agg = uploadRule.latestAgg;
                              const refMk = String(val(agg, 'uploadUsageReferenceMonthKey') ?? '');
                              const [y, m] = refMk.split('-');
                              const exceeded = !!val(agg, 'uploadAverageExceeded');
                              return (
                                <Paper elevation={0} sx={{ p: 1.5, textAlign: 'center', flex: 2, minWidth: 180, border: `1px solid ${exceeded ? '#ed6c02' : '#2e7d32'}`, borderRadius: 1 }}>
                                  <Typography variant="caption" sx={{ fontSize: 10, color: '#666' }}>
                                    {t('LastMonthAverageUploads', { month: y && m ? `${m}/${y}` : '—' })}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13 }}>
                                      {Number(val(agg, 'uploadAveragePerClient') ?? 0).toLocaleString('he-IL')}
                                    </Typography>
                                    <Chip size="small" color={exceeded ? 'warning' : 'success'} label={exceeded ? t('UploadAverageCharged') : t('UploadAverageNotCharged')} sx={{ height: 20, fontSize: 10 }} />
                                  </Box>
                                  <Typography variant="caption" sx={{ fontSize: 10, color: '#666' }}>
                                    {t('UploadAverageCalc', {
                                      total: Number(val(agg, 'uploadTotalUsed') ?? 0).toLocaleString('he-IL'),
                                      clients: Number(val(agg, 'uploadAverageClientsCount') ?? 0).toLocaleString('he-IL'),
                                    })}
                                  </Typography>
                                </Paper>
                              );
                            })()}
                          </Box>
                          <Typography variant="body2" sx={{ fontSize: 12, lineHeight: 1.6 }}>
                            {uploadRule.limit === 0
                              ? t('UploadChargeRuleExplanationAlways', { maxUploads: uploadRule.maxUploads })
                              : t('UploadChargeRuleExplanation', { limit: uploadRule.limit, maxUploads: uploadRule.maxUploads })}
                          </Typography>
                          {uploadRule.limit !== 0 && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#6B7280', lineHeight: 1.5 }}>
                              {t('UploadChargeRuleExample', { maxUploads: uploadRule.maxUploads })}
                            </Typography>
                          )}
                        </>
                      )}
                    </Paper>
                  )}
                  {!calcLoading && !isAccountant && calcTotals && (
                    <Paper elevation={1} sx={{ p: 2, borderTop: '3px solid #1976d2', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: 13 }}>סיכום לתקופה</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <SummaryTile label={t('Charges') || 'חיובים'} value={calcTotals?.totalCharges ?? calcTotals?.TotalCharges} />
                        <SummaryTile label={t('OneTimeCharges') || 'חד פעמי'} value={calcTotals?.totalOneTimeCharges ?? calcTotals?.TotalOneTimeCharges} />
                        <SummaryTile label={t('Refunds') || 'החזרים'} value={calcTotals?.totalRefunds ?? calcTotals?.TotalRefunds} />
                        <SummaryTile label={t('Discounts') || 'הנחות'} value={calcTotals?.totalDiscounts ?? calcTotals?.TotalDiscounts} />
                        <SummaryTile label={t('Payments') || 'תשלומים'} value={calcTotals?.totalPayments ?? calcTotals?.TotalPayments} />
                        <SummaryTile label={t('NetBalance') || 'יתרה'} value={calcTotals?.netBalance ?? calcTotals?.NetBalance} color={getNetBalanceColor(calcTotals?.netBalance ?? calcTotals?.NetBalance)} />
                      </Box>
                    </Paper>
                  )}
                  {!calcLoading && calcData?.months?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: 13, color: '#1976d2' }}>
                        {t('arrangementView.chargesDetails.accountantPayerMonthlyBreakdown') || 'פירוט חיובים חודשי'}
                      </Typography>
                      <ChargesDetailsPanel calcData={calcData} userType={userType} />
                    </Box>
                  )}
                </>
              )}
              <Paper elevation={1} sx={{ p: 2, borderTop: '3px solid #666' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CreditCardIcon sx={{ fontSize: 18 }} /> {t('PaymentMethod') || 'אמצעי תשלום'}
                </Typography>
                <UpdateOrAddPaymentMethod userId={userId} userType={+userType} hasPaymentMethod={!!(arrangement ? hasCreditCard : status?.hasArrangementPaymentMethod)} onSaved={refreshStatus} />
              </Paper>
              <Paper elevation={1} sx={{ p: 2, mt: 2, borderTop: '3px solid #1565c0' }}>
                <MasavMethodEditor userId={userId} userType={+userType} arrangement={arrangement} onSaved={() => { loadArrangement(); refreshStatus && refreshStatus(); }} />
              </Paper>
            </Box>
          )}

          {/* ───── Tab: Clients (accountant only) ───── */}
          {tabKey === 'clients' && isAccountant && (
            <Box>
              {clientsLoading ? (
                <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
              ) : clientsError ? (
                <Alert severity="error">{clientsError}</Alert>
              ) : (
                <>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                    <Paper elevation={0} sx={{ px: 1.5, py: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">{t('Clients')}</Typography>
                      <Typography variant="body2" fontWeight={700}>{officeClients.length}</Typography>
                    </Paper>
                    <Paper elevation={0} sx={{ px: 1.5, py: 1, border: '1px solid #ed6c02', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">{t('arrangementView.chargesDetails.clientPayerTab') || 'לקוח משלם'}</Typography>
                      <Typography variant="body2" fontWeight={700}>{selfPaidClients.length}</Typography>
                    </Paper>
                    <Paper elevation={0} sx={{ px: 1.5, py: 1, border: '1px solid #1976d2', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">{t('arrangementView.chargesDetails.accountantPayerTab') || 'רו״ח משלם'}</Typography>
                      <Typography variant="body2" fontWeight={700}>{accountantPaidClients.length}</Typography>
                    </Paper>
                    <Paper elevation={0} sx={{ px: 1.5, py: 1, border: '1px solid #2e7d32', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">{t('CreditCard')}</Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {selfPaidClients.filter((c) => !!(c.hasCreditCard ?? c.HasCreditCard)).length}
                      </Typography>
                    </Paper>
                    <Paper elevation={0} sx={{ px: 1.5, py: 1, border: '1px solid #ed6c02', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">{t('unpaid') || 'לא שולם'}</Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {selfPaidClients.filter((c) => !(c.hasCreditCard ?? c.HasCreditCard)).length}
                      </Typography>
                    </Paper>
                  </Box>

                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5 }}>
                    <Tabs
                      value={clientsSubTab}
                      onChange={(_, v) => { setClientsSubTab(v); setPage(0); }}
                      variant="scrollable"
                      scrollButtons="auto"
                    >
                      <Tab
                        label={`${t('arrangementView.chargesDetails.clientPayerTab') || 'לקוח משלם'} (${selfPaidClients.length})`}
                        sx={{ fontSize: 13, minHeight: 40 }}
                      />
                      <Tab
                        label={`${t('arrangementView.chargesDetails.accountantPayerTab') || 'רו״ח משלם'} (${accountantPaidClients.length})`}
                        sx={{ fontSize: 13, minHeight: 40 }}
                      />
                    </Tabs>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TextField
                      size="small"
                      placeholder={t('Search') || 'חיפוש...'}
                      value={clientsSearch}
                      onChange={(e) => { setClientsSearch(e.target.value); setPage(0); }}
                      sx={{ minWidth: 220 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18 }} />
                          </InputAdornment>
                        ),
                        sx: { fontSize: 13 },
                      }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={clientsExportingPdf ? <CircularProgress size={14} /> : <PictureAsPdfIcon />}
                      disabled={clientsExportingPdf || clientsExportingExcel || clientsLoading || officeClients.length === 0}
                      onClick={handleExportClientsPdf}
                    >
                      {t('DownloadPdf') || 'PDF'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={clientsExportingExcel ? <CircularProgress size={14} /> : <TableChartIcon />}
                      disabled={clientsExportingPdf || clientsExportingExcel || clientsLoading || officeClients.length === 0}
                      onClick={handleExportClientsExcel}
                    >
                      {t('DownloadExcel') || 'Excel'}
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      {filteredClientsForSubTab.length} {t('Clients')}
                    </Typography>
                  </Box>

                  {filteredClientsForSubTab.length === 0 ? (
                    <Alert severity="info">{t('NoClients') || 'אין לקוחות'}</Alert>
                  ) : (
                    <>
                      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={headerSx}>{t('Name')}</TableCell>
                              <TableCell sx={headerSx}>{t('CaseId') || 'מזהה'}</TableCell>
                              <TableCell sx={headerSx}>{t('CaseType') || 'סוג עסק'}</TableCell>
                              <TableCell sx={headerSx}>{t('PaymentMethod')}</TableCell>
                              <TableCell sx={headerSx}>{t('CreditCard')}</TableCell>
                              <TableCell sx={headerSx}>{t('PaymentStatus') || 'סטטוס תשלום'}</TableCell>
                              <TableCell sx={headerSx}>{t('Status')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginatedClients.map((c) => {
                              const caseId = c.caseId ?? c.CaseId;
                              const paysSelf = isClientPayer(c.clientPaymentMethod ?? c.ClientPaymentMethod);
                              const hasCard = !!(c.hasCreditCard ?? c.HasCreditCard);
                              return (
                                <TableRow key={caseId} hover>
                                  <TableCell sx={cellSx}>{c.name ?? c.Name ?? '—'}</TableCell>
                                  <TableCell sx={cellSx}>{caseId}</TableCell>
                                  <TableCell sx={cellSx}>{c.caseType ?? c.CaseType ?? '—'}</TableCell>
                                  <TableCell sx={cellSx}>
                                    {paysSelf
                                      ? <Chip size="small" color="warning" variant="outlined" label={t('arrangementView.chargesDetails.clientPayerTab') || 'לקוח משלם'} sx={{ height: 20, fontSize: 10 }} />
                                      : <Chip size="small" color="primary" variant="outlined" label={t('arrangementView.chargesDetails.accountantPayerTab') || 'רו״ח משלם'} sx={{ height: 20, fontSize: 10 }} />}
                                  </TableCell>
                                  <TableCell sx={cellSx}>
                                    {paysSelf
                                      ? (hasCard
                                        ? <Chip size="small" color="success" icon={<CreditCardIcon sx={{ fontSize: 14 }} />} label={t('Yes')} sx={{ height: 20, fontSize: 10 }} />
                                        : <Chip size="small" color="default" variant="outlined" label={t('No')} sx={{ height: 20, fontSize: 10 }} />)
                                      : <Typography component="span" sx={{ fontSize: 12, color: '#999' }}>—</Typography>}
                                  </TableCell>
                                  <TableCell sx={cellSx}>
                                    {paysSelf
                                      ? renderClientPaymentStatusChip(c)
                                      : <Typography component="span" sx={{ fontSize: 12, color: '#999' }}>—</Typography>}
                                  </TableCell>
                                  <TableCell sx={cellSx}>
                                    {(c.isActive ?? c.IsActive)
                                      ? <Chip size="small" color="success" label={t('Active')} sx={{ height: 20, fontSize: 10 }} />
                                      : <Chip size="small" color="default" label={t('Inactive')} sx={{ height: 20, fontSize: 10 }} />}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={filteredClientsForSubTab.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                        labelRowsPerPage={t('RowsPerPage') || 'שורות בעמוד'}
                      />
                    </>
                  )}
                </>
              )}
            </Box>
          )}

          {/* ───── Tab: Payments ───── */}
          {tabKey === 'payments' && (
            <Box>
              {arrangementLoading ? (
                <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
              ) : !arrangement ? (
                <Alert severity="info">{t('NoArrangement') || 'לא נמצא הסדר חיוב'}</Alert>
              ) : (
                <>
                  <Box sx={{ mb: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, display: 'flex', gap: 3 }}>
                    <Typography variant="body2"><strong>{t('TotalPayments')}</strong> {payments.length}</Typography>
                    <Typography variant="body2"><strong>{t('TotalAmount')}</strong> {fmtCurrency(paymentsTotal)}</Typography>
                  </Box>
                  {payments.length === 0 ? (
                    <Alert severity="info">{t('NoPayments') || 'אין תשלומים'}</Alert>
                  ) : (
                    <>
                      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={headerSx}>{t('Date')}</TableCell>
                              <TableCell sx={headerSx}>{t('PaymentMethod')}</TableCell>
                              <TableCell sx={headerSx} align="right">{t('Amount')}</TableCell>
                              <TableCell sx={headerSx}>{t('Reference')}</TableCell>
                              <TableCell sx={headerSx}>{t('Invoice')}</TableCell>
                              <TableCell sx={headerSx}>{t('Status')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginatedPayments.map((p) => (
                              <TableRow key={val(p, 'id') || p.id}>
                                <TableCell sx={cellSx}>{fmtDate(val(p, 'paymentDate'))}</TableCell>
                                <TableCell sx={cellSx}>{t(getPaymentMethodText(val(p, 'paymentMethod')))}</TableCell>
                                <TableCell sx={cellSx} align="right">{fmtCurrency(val(p, 'amount'))}</TableCell>
                                <TableCell sx={cellSx}>{val(p, 'referenceNumber') || '—'}</TableCell>
                                <TableCell sx={cellSx}>{renderPaymentDocuments(val(p, 'id'))}</TableCell>
                                <TableCell sx={cellSx}>
                                  <Chip size="small" color={val(p, 'isConfirmed') ? 'success' : 'warning'} label={val(p, 'isConfirmed') ? t('Confirmed') : t('Pending')} />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={payments.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                        labelRowsPerPage={t('RowsPerPage') || 'שורות בעמוד'}
                      />
                    </>
                  )}
                </>
              )}
            </Box>
          )}

          {/* ───── Tab: Refunds ───── */}
          {tabKey === 'refunds' && (
            <Box>
              {arrangementLoading ? (
                <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
              ) : !arrangement ? (
                <Alert severity="info">{t('NoArrangement') || 'לא נמצא הסדר חיוב'}</Alert>
              ) : (
                <>
                  <Box sx={{ mb: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, display: 'flex', gap: 3 }}>
                    <Typography variant="body2"><strong>{t('TotalRefunds')}</strong> {refunds.length}</Typography>
                    <Typography variant="body2"><strong>{t('TotalAmount')}</strong> {fmtCurrency(refundsTotal)}</Typography>
                  </Box>
                  {refunds.length === 0 ? (
                    <Alert severity="info">{t('NoRefunds') || 'אין החזרים'}</Alert>
                  ) : (
                    <>
                      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={headerSx}>{t('Date')}</TableCell>
                              <TableCell sx={headerSx} align="right">{t('Amount')}</TableCell>
                              <TableCell sx={headerSx}>{t('CreditNote')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginatedRefunds.map((r) => (
                              <TableRow key={val(r, 'id') || r.id}>
                                <TableCell sx={cellSx}>{fmtDate(val(r, 'refundDate'))}</TableCell>
                                <TableCell sx={cellSx} align="right">{fmtCurrency(val(r, 'amount'))}</TableCell>
                                <TableCell sx={cellSx}>{renderRefundDocuments(val(r, 'id'))}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={refunds.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                        labelRowsPerPage={t('RowsPerPage') || 'שורות בעמוד'}
                      />
                    </>
                  )}
                </>
              )}
            </Box>
          )}

          {/* ───── Tab: Discounts ───── */}
          {tabKey === 'discounts' && (
            <Box>
              {arrangementLoading ? (
                <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
              ) : !arrangement ? (
                <Alert severity="info">{t('NoArrangement') || 'לא נמצא הסדר חיוב'}</Alert>
              ) : (
                <>
                  {discounts.length === 0 ? (
                    <Alert severity="info">{t('NoDiscounts') || 'אין הנחות'}</Alert>
                  ) : (
                    <>
                      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={headerSx}>{t('Value') || 'ערך'}</TableCell>
                              <TableCell sx={headerSx}>{t('StartMonth') || 'חודש התחלה'}</TableCell>
                              <TableCell sx={headerSx}>{t('EndMonth') || 'חודש סיום'}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginatedDiscounts.map((d) => (
                              <TableRow key={val(d, 'id') || d.id}>
                                <TableCell sx={cellSx}>{val(d, 'isPercentage') ? `${val(d, 'value') ?? 0}%` : fmtCurrency(val(d, 'value'))}</TableCell>
                                <TableCell sx={cellSx}>{val(d, 'startMonthKey') || '—'}</TableCell>
                                <TableCell sx={cellSx}>{val(d, 'endMonthKey') || '—'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={discounts.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                        labelRowsPerPage={t('RowsPerPage') || 'שורות בעמוד'}
                      />
                    </>
                  )}
                </>
              )}
            </Box>
          )}

          {/* ───── Tab: OTC ───── */}
          {tabKey === 'otc' && (
            <Box>
              {arrangementLoading ? (
                <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
              ) : !arrangement ? (
                <Alert severity="info">{t('NoArrangement') || 'לא נמצא הסדר חיוב'}</Alert>
              ) : (
                <>
                  <Box sx={{ mb: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, display: 'flex', gap: 3 }}>
                    <Typography variant="body2"><strong>{t('OneTimeCharges')}</strong> {onTimeCharges.length}</Typography>
                    <Typography variant="body2"><strong>{t('TotalAmount')}</strong> {fmtCurrency(otcTotal)}</Typography>
                  </Box>
                  {onTimeCharges.length === 0 ? (
                    <Alert severity="info">{t('NoOneTimeCharges') || 'אין חיובים חד פעמיים'}</Alert>
                  ) : (
                    <>
                      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={headerSx}>{t('Description') || 'תיאור'}</TableCell>
                              <TableCell sx={headerSx}>{t('Date')}</TableCell>
                              <TableCell sx={headerSx} align="right">{t('Amount')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginatedOtc.map((otc) => (
                              <TableRow key={val(otc, 'id') || otc.id}>
                                <TableCell sx={cellSx}>{val(otc, 'description') || '—'}</TableCell>
                                <TableCell sx={cellSx}>{fmtDate(val(otc, 'chargeDate'))}</TableCell>
                                <TableCell sx={cellSx} align="right">{fmtCurrency(val(otc, 'amount'))}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={onTimeCharges.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                        labelRowsPerPage={t('RowsPerPage') || 'שורות בעמוד'}
                      />
                    </>
                  )}
                </>
              )}
            </Box>
          )}

          {/* ───── Tab: Saved cycles ───── */}
          {tabKey === 'cycles' && (
            <Box>
              {arrangementLoading ? (
                <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
              ) : !arrangement ? (
                <Alert severity="info">{t('NoArrangement') || 'לא נמצא הסדר חיוב'}</Alert>
              ) : (
                <>
                  {sortedPlanPeriods.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{t('PlanPeriods') || 'תקופות תוכנית'} ({sortedPlanPeriods.length})</Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', maxHeight: 220 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={headerSx}>{t('Plan') || 'תוכנית'}</TableCell>
                              <TableCell sx={headerSx}>{t('Start') || 'התחלה'}</TableCell>
                              <TableCell sx={headerSx}>{t('End') || 'סיום'}</TableCell>
                              <TableCell sx={headerSx}>{t('Status')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedPlanPeriods.map((pp) => (
                              <TableRow key={val(pp, 'id') || pp.id}>
                                <TableCell sx={cellSx}>{val(pp, 'monthlyPlanName') || val(pp, 'packagePlanName') || '—'}</TableCell>
                                <TableCell sx={cellSx}>{val(pp, 'startMonthKey') || val(pp, 'startDateFormatted') || '—'}</TableCell>
                                <TableCell sx={cellSx}>{val(pp, 'endMonthKey') || val(pp, 'endDateFormatted') || '—'}</TableCell>
                                <TableCell sx={cellSx}><Chip size="small" color={val(pp, 'isActive') ? 'success' : 'default'} label={val(pp, 'isActive') ? t('Active') : t('Inactive')} /></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{t('BillingCycles') || 'מחזורי חיוב'} ({calcMonthsCount})</Typography>
                  {calcLoading ? (
                    <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
                  ) : calcMonthsCount === 0 ? (
                    <Alert severity="info">{t('NoSavedCycles') || 'אין מחזורים שמורים'}</Alert>
                  ) : (
                    <ChargesDetailsPanel calcData={calcData} userType={userType} />
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={onClose}>{t('Close')}</Button>
          <Button variant="outlined" startIcon={pdfDownloading ? <CircularProgress size={18} /> : <PictureAsPdfIcon />} disabled={pdfDownloading || excelDownloading} onClick={handleDownloadPdf}>
            {pdfDownloading ? (t('Downloading') || 'Downloading…') : (t('DownloadPdf') || 'Download PDF')}
          </Button>
          <Button variant="outlined" startIcon={excelDownloading ? <CircularProgress size={18} /> : <TableChartIcon />} disabled={pdfDownloading || excelDownloading} onClick={handleDownloadExcel}>
            {excelDownloading ? (t('Downloading') || 'Downloading…') : (t('DownloadExcel') || 'Download Excel')}
          </Button>
          <UpdateOrAddPaymentMethod userId={userId} userType={+userType} hasPaymentMethod={!!(arrangement ? hasCreditCard : status?.hasArrangementPaymentMethod)} onSaved={refreshStatus} />
          <Button variant="contained" color="error" disabled={cancelling} startIcon={cancelling && <CircularProgress size={18} />} onClick={onCancel}>
            {t('CancelSubscription')}
          </Button>
        </DialogActions>
      </Box>
    </Modal>
  );
};

PaymentStatusDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  status: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  cancelling: PropTypes.bool,
  userId: PropTypes.string.isRequired,
  userType: PropTypes.number.isRequired,
  refreshStatus: PropTypes.func.isRequired
};

export default PaymentStatusDialog;
